import React, { useEffect, useMemo, useRef, useState } from "react";

/**
 * BreathingPacer — visible pulsing breathing pacer.
 *
 * Phases (default 4-7-8, slowed slightly for screen visibility):
 *   inhale  → 4s (circle grows)
 *   hold    → 4s (circle stays full)
 *   exhale  → 6s (circle shrinks)
 *   rest    → 2s (circle stays small)
 *
 * Pattern can be swapped via the `pattern` prop.
 *
 * Renders absolutely-positioned over the session content but does NOT
 * intercept clicks (pointer-events: none).
 */
const PATTERNS = {
  "4-4-6-2": [4, 4, 6, 2],
  "4-7-8": [4, 7, 8, 0],
  "5-5":   [5, 0, 5, 0],
  "box":   [4, 4, 4, 4],
};

const PHASE_LABELS = ["Breathe in", "Hold", "Breathe out", "Rest"];

export default function BreathingPacer({
  active = true,
  pattern = "4-4-6-2",
  size = 220,
  onPhaseChange = null,
}) {
  const phases = useMemo(() => PATTERNS[pattern] || PATTERNS["4-4-6-2"], [pattern]);
  const totalCycle = useMemo(
    () => phases.reduce((a, b) => a + b, 0),
    [phases]
  );

  const [t, setT] = useState(0); // seconds within the cycle

  useEffect(() => {
    if (!active) return;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const elapsed = (now - start) / 1000;
      setT(elapsed % totalCycle);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active, totalCycle]);

  // resolve current phase + progress within phase
  let cumulative = 0;
  let phaseIdx = 0;
  let phaseProgress = 0;
  for (let i = 0; i < phases.length; i++) {
    if (t < cumulative + phases[i]) {
      phaseIdx = i;
      phaseProgress = phases[i] > 0 ? (t - cumulative) / phases[i] : 1;
      break;
    }
    cumulative += phases[i];
  }

  // Notify parent on phase transitions. We skip zero-duration phases (e.g.
  // hold=0 in the 5-5 pattern) and fire onPhaseChange once per real entry.
  const lastPhaseRef = useRef(-1);
  useEffect(() => {
    if (!active || !onPhaseChange) return;
    if (phaseIdx === lastPhaseRef.current) return;
    if (phases[phaseIdx] <= 0) return;
    lastPhaseRef.current = phaseIdx;
    onPhaseChange(phaseIdx, phases[phaseIdx]);
  }, [phaseIdx, active, phases, onPhaseChange]);

  // Scale: starts small, grows during inhale, full during hold, shrinks during exhale, small during rest
  let scale;
  switch (phaseIdx) {
    case 0: // inhale
      scale = 0.55 + 0.45 * easeInOut(phaseProgress);
      break;
    case 1: // hold
      scale = 1.0;
      break;
    case 2: // exhale
      scale = 1.0 - 0.45 * easeInOut(phaseProgress);
      break;
    default: // rest
      scale = 0.55;
  }

  const label = PHASE_LABELS[phaseIdx];
  const remaining = Math.max(0, phases[phaseIdx] - (t - cumulative));

  return (
    <div
      className="pointer-events-none select-none flex flex-col items-center justify-center"
      data-testid="breathing-pacer"
      style={{ width: size, height: size + 70 }}
    >
      <div
        style={{
          width: size,
          height: size,
          borderRadius: "9999px",
          background:
            "radial-gradient(circle, rgba(0,191,255,0.30) 0%, rgba(0,191,255,0.08) 55%, rgba(0,191,255,0) 75%)",
          border: "1px solid rgba(0,191,255,0.45)",
          boxShadow:
            "0 0 60px rgba(0,191,255,0.35), inset 0 0 60px rgba(0,191,255,0.25)",
          transform: `scale(${scale})`,
          transition: "transform 120ms linear",
        }}
      />
      <div className="mt-3 text-center">
        <div className="font-mono text-[10px] tracking-[0.32em] uppercase text-[color:var(--text-secondary)]">
          {pattern}
        </div>
        <div className="text-ink text-lg font-light tracking-wide">
          {label}
          <span className="text-cyan-glow font-mono ml-2">
            {phases[phaseIdx] > 0 ? Math.ceil(remaining) : ""}
          </span>
        </div>
      </div>
    </div>
  );
}

function easeInOut(x) {
  return x < 0.5 ? 2 * x * x : 1 - Math.pow(-2 * x + 2, 2) / 2;
}
